import { Grid, Step, StepLabel, Stepper } from '@mui/material';
import { useState } from 'react';
import {
  useHistory,
  useParams,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { values } from 'lodash';
import { ActionStatus } from 'further-types/lp/action';
import { LP_CAPITAL_ACTION_CREATE } from 'adminConstants/routes';
import {
  createLpCapitalActionEditRoute,
  createLpCapitalActionSuccessRoute,
  LP_CAPITAL_ACTION_LIST,
  EQUALISATIONS_ADD,
} from 'adminConstants/routes';
import ErrorBoundary from 'components/ErrorBoundary';
import Page from 'components/new/Page';
import PageLoader from 'components/PageLoader';
import FormWithStateContextProvider from 'contexts/FormWithStateContext';
import { useLayoutContext } from 'contexts/LayoutContext';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import api from 'lib/trpcClient';
import CapitalActionDetails from './CapitalActionDetails';
import CapitalActionTransactions from './CapitalActionEntries';
import CapitalActionReview from './CapitalActionReview';
import useCapitalActionForm, { Steps } from './hooks/useCapitalActionForm';

const stepLabels = (isCreateRoute: boolean) => ({
  [Steps.Details]: isCreateRoute
    ? 'Create capital action'
    : 'Edit capital action',
  [Steps.Transactions]: 'Add transactions',
  [Steps.Review]: 'Approve action',
});

const stepToUrlParam = {
  [Steps.Details]: 'details',
  [Steps.Transactions]: 'transactions',
  [Steps.Review]: 'review',
};

const urlParamToStep = {
  details: Steps.Details,
  transactions: Steps.Transactions,
  review: Steps.Review,
};

type StepUrlParam = keyof typeof urlParamToStep;

const isValidStepParam = (param: string | null): param is StepUrlParam =>
  param !== null && param in urlParamToStep;

type Props = {
  vehicleId: string;
};

const AddEditCapitalAction: React.FC<Props> = ({ vehicleId }) => {
  const history = useHistory();
  const location = useLocation();
  const isCreateRoute = !!useRouteMatch(LP_CAPITAL_ACTION_CREATE)?.isExact;
  const { id } = useParams<{ id?: string }>();

  const urlParams = new URLSearchParams(location.search);
  const stepParam = urlParams.get('step');
  const [activeStep, setActiveStep] = useState<Steps>(
    isValidStepParam(stepParam) ? urlParamToStep[stepParam] : Steps.Details,
  );

  const [overpaymentPerCommitment] =
    api.capital.overpaymentPerCommitment.useSuspenseQuery({
      vehicleId,
    });

  const [listUncalledEqualisations] =
    api.capital.listUncalledEqualisations.useSuspenseQuery({
      vehicleId,
    });

  const { data: capitalAction } = api.capitalAction.get.useQuery(
    {
      id: id!,
      vehicleId,
    },
    {
      enabled: !!id,
    },
  );

  const [commitments] = api.commitments.list.useSuspenseQuery({
    vehicleId,
    order: 'desc',
    page: 1,
    rowsPerPage: 1000,
    orderBy: 'commitmentDate',
  });
  const hasUncalledEqualisations = (listUncalledEqualisations?.length ?? 0) > 0;

  const { formMethods, submitStep, isLoading, lastAction, setLastAction } =
    useCapitalActionForm({
      id,
      vehicleId,
      defaultValues: capitalAction,
      hasUncalledEqualisations,
    });

  const hasPendingCommitments = commitments.results?.some(
    (commitment) => !commitment.equalisationId,
  );

  const commitmentsPendingDialog = useDisclosure(
    !id && activeStep === Steps.Details && hasPendingCommitments,
  );

  const handleCancel = () => {
    history.push(LP_CAPITAL_ACTION_LIST);
  };

  const handleSaveAndExit = () => {
    setLastAction('saveAndExit');
    submitStep(
      activeStep,
      () => {
        history.push(LP_CAPITAL_ACTION_LIST);
      },
      { approve: false },
    );
  };

  const handleNext = () => {
    setLastAction('saveAndNext');
    submitStep(activeStep, ({ id }) => {
      setActiveStep(activeStep + 1);

      history.push(
        `${createLpCapitalActionEditRoute(id)}?step=${
          stepToUrlParam[activeStep + 1]
        }`,
      );
    });
  };

  const handleSubmit = () => {
    setLastAction('saveAndNext');
    submitStep(activeStep, (capitalAction) => {
      if (capitalAction.status === ActionStatus.Approved) {
        history.push(createLpCapitalActionSuccessRoute(capitalAction.id));
      }
    });
  };

  const handlePrevious = () => {
    setActiveStep(activeStep - 1);

    if (id) {
      history.push(
        `${createLpCapitalActionEditRoute(id)}?step=${
          stepToUrlParam[activeStep - 1]
        }`,
      );
    }
  };

  const allocations = Object.fromEntries(
    capitalAction?.entries.map((entry, index) => [
      index,
      entry.capitalBreakdown,
    ]) ?? [],
  );

  const hasOverpayments = values(overpaymentPerCommitment ?? {}).some(
    (overpayment) => !!overpayment,
  );

  const messages = hasUncalledEqualisations
    ? [
        {
          key: 'uncalled-equalisations',
          content:
            'This vehicle has an approved equalisation pending call. The equalisation must be the first item added to this capital action.',
          variant: 'info' as const,
          centered: true,
        },
      ]
    : hasOverpayments
    ? [
        {
          key: 'overpayments',
          content:
            'There are overpayments for some investors. Please review the overpayments and adjust the capital action accordingly.',
          variant: 'info' as const,
          centered: true,
        },
      ]
    : [];

  return (
    <Page messages={messages}>
      <Column spacing="lg" fullHeight>
        <Grid container>
          <Grid size={{ sm: 12, md: 12, lg: 8 }}>
            <Stepper activeStep={activeStep}>
              {values(stepLabels(isCreateRoute)).map((stepLabel) => {
                return (
                  <Step key={stepLabel}>
                    <StepLabel>{stepLabel}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Grid>
        </Grid>
        <FormWithStateContextProvider
          formMethods={formMethods}
          initialState={{ allocations }}
        >
          <ErrorBoundary loader={<PageLoader />}>
            {activeStep === Steps.Details && (
              <CapitalActionDetails
                lastAction={lastAction}
                onCancel={handleCancel}
                onSaveAndExit={handleSaveAndExit}
                onNext={handleNext}
                vehicleId={vehicleId}
                isLoading={isLoading}
              />
            )}
            {activeStep === Steps.Transactions && (
              <CapitalActionTransactions
                lastAction={lastAction}
                onPrevious={handlePrevious}
                onSaveAndExit={handleSaveAndExit}
                onNext={handleNext}
                hasUncalledEqualisations={hasUncalledEqualisations}
                isLoading={isLoading}
              />
            )}
            {activeStep === Steps.Review && (
              <CapitalActionReview
                lastAction={lastAction}
                onPrevious={handlePrevious}
                onSaveAndExit={handleSaveAndExit}
                onSubmit={handleSubmit}
                isLoading={isLoading}
              />
            )}
          </ErrorBoundary>
        </FormWithStateContextProvider>
      </Column>
      <ConfirmDialog
        open={commitmentsPendingDialog.isOpen}
        onClose={commitmentsPendingDialog.onClose}
        title="Commitments pending close"
        content="There are commitments pending close. If you intend to include these commitments in this capital action, you must close and equalise those commitments before proceeding."
        actions={{
          confirm: {
            label: 'Close and equalise',
            onClick: () => {
              history.push(EQUALISATIONS_ADD);
            },
          },
          cancel: {
            label: 'Ignore and continue with capital action',
            onClick: commitmentsPendingDialog.onClose,
          },
        }}
        maxWidth="sm"
      />
    </Page>
  );
};

const AddEditCapitalActionWrapper: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();

  if (!selectedVehicleId) {
    return null;
  }

  return <AddEditCapitalAction vehicleId={selectedVehicleId} />;
};

export default AddEditCapitalActionWrapper;
